import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { signOutUser } from '../utils/firebase';
import { Link } from './common/Link';
import { NavLink } from './navigation/NavLink';
import { Text } from './typography/Text';
import { SubscriptionBadge } from './common/SubscriptionBadge';
import { Menu, X, ChevronDown, ChevronUp } from 'lucide-react';
import { AuthModal } from './AuthModal';
import { PromoBar } from './PromoBar';

export function Header() {
  const { user, subscription } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isToolsDropdownOpen, setIsToolsDropdownOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPromoBar, setShowPromoBar] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const tools = [];

  const hasActivePlan = subscription?.status === 'active' || subscription?.status === 'trialing';

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsToolsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div>
      {(!user || !hasActivePlan) && showPromoBar && (
        <PromoBar onClose={() => setShowPromoBar(false)} />
      )}
      <header 
        className="fixed left-0 right-0 z-50 bg-white border-b border-gray-100"
        style={{ top: (!user || !hasActivePlan) && showPromoBar ? '40px' : '0' }}
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link href="/" className="font-gabarito font-bold text-black text-xl">
                hiiroo
              </Link>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden p-2 text-gray-600 hover:text-gray-900"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>

              {/* Desktop navigation */}
              <nav className="hidden lg:flex flex-1 justify-center">
                <div className="flex items-center space-x-8">
                  {tools.length > 0 && (
                    <div className="relative" ref={dropdownRef}>
                      <button
                        onClick={() => setIsToolsDropdownOpen(!isToolsDropdownOpen)}
                        className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gradient-to-r from-primary/5 to-primary/10 border border-transparent hover:border-primary/20 transition-all duration-300 font-gabarito"
                      >
                        <span className="text-slate">Other Tools</span>
                        {isToolsDropdownOpen ? (
                          <ChevronUp className="w-4 h-4 text-primary" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-primary" />
                        )}
                      </button>

                      {isToolsDropdownOpen && (
                        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg py-2 border border-primary/10">
                          {tools.map((tool) => (
                            <Link
                              key={tool.href}
                              href={tool.href}
                              className="block px-4 py-2 text-sm text-slate hover:bg-primary/5 transition-colors font-gabarito"
                              onClick={() => setIsToolsDropdownOpen(false)}
                            >
                              {tool.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  <NavLink href="/pricing" className="font-gabarito">Pricing</NavLink>
                  <NavLink href="/about" className="font-gabarito">About</NavLink>
                  <NavLink href="/faq" className="font-gabarito">FAQ's</NavLink>
                  <NavLink href="/contact" className="font-gabarito">Contacts</NavLink>
                </div>
              </nav>

              {/* Auth buttons */}
              <div className="hidden lg:flex items-center space-x-4">
                {user ? (
                  <div className="flex items-center space-x-4">
                    <SubscriptionBadge />
                    <Link
                      href="/dashboard"
                      className="flex items-center space-x-2 bg-gray-50 rounded-full px-4 py-2 hover:bg-gray-100 transition-colors font-gabarito"
                    >
                      <img
                        src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.email || '')}`}
                        alt={user.displayName || 'User'}
                        className="w-6 h-6 rounded-full"
                      />
                      <Text className="text-slate">Dashboard</Text>
                    </Link>
                    {hasActivePlan ? (
                      <button
                        onClick={signOutUser}
                        className="text-slate hover:text-slate-light transition-colors font-gabarito"
                      >
                        Sign Out
                      </button>
                    ) : (
                      <Link
                        href="/pricing"
                        className="bg-purple-gradient text-white px-5 py-2 rounded-full text-sm hover:bg-purple-gradient-hover transition-colors font-gabarito"
                      >
                        Upgrade
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center space-x-4">
                    <Link
                      href="/pricing"
                      className="bg-purple-gradient text-white px-5 py-2 rounded-full text-sm hover:bg-purple-gradient-hover transition-colors font-gabarito"
                    >
                      Upgrade
                    </Link>
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="bg-black text-white px-5 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors font-gabarito"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile navigation */}
            <div className={`lg:hidden ${isMobileMenuOpen ? 'block' : 'hidden'} pt-4 pb-2`}>
              <nav className="flex flex-col space-y-4 font-gabarito">
                {tools.map((tool) => (
                  <Link
                    key={tool.href}
                    href={tool.href}
                    className="text-slate hover:text-primary transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {tool.label}
                  </Link>
                ))}
                <NavLink href="/pricing">Pricing</NavLink>
                <NavLink href="/about">About</NavLink>
                <NavLink href="/faq">FAQ's</NavLink>
                <NavLink href="/contact">Contacts</NavLink>
                {user ? (
                  <div className="flex flex-col space-y-4 pt-4 border-t">
                    <SubscriptionBadge />
                    <Link
                      href="/dashboard"
                      className="flex items-center space-x-2 bg-gray-50 rounded-full px-4 py-2 transition-colors hover:bg-gray-100"
                    >
                      <img
                        src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.email || '')}`}
                        alt={user.displayName || 'User'}
                        className="w-5 h-5 rounded-full"
                      />
                      <Text className="text-slate">Dashboard</Text>
                    </Link>
                    {hasActivePlan ? (
                      <button
                        onClick={signOutUser}
                        className="text-slate hover:text-slate-light transition-colors"
                      >
                        Sign Out
                      </button>
                    ) : (
                      <Link
                        href="/pricing"
                        className="bg-purple-gradient text-white px-5 py-2 rounded-full text-sm hover:bg-purple-gradient-hover transition-colors text-center"
                      >
                        Upgrade
                      </Link>
                    )}
                  </div>
                ) : (
                  <div className="flex flex-col space-y-4 pt-4 border-t">
                    <Link
                      href="/pricing"
                      className="bg-purple-gradient text-white px-5 py-2 rounded-full text-sm hover:bg-purple-gradient-hover transition-colors text-center"
                    >
                      Upgrade
                    </Link>
                    <button
                      onClick={() => setShowAuthModal(true)}
                      className="bg-black text-white px-5 py-2 rounded-full text-sm hover:bg-gray-800 transition-colors w-full"
                    >
                      Sign In
                    </button>
                  </div>
                )}
              </nav>
            </div>
          </div>
        </div>
      </header>

      {/* Spacer to prevent content from going under fixed header */}
      <div 
        style={{ 
          height: '72px',
          marginTop: (!user || !hasActivePlan) && showPromoBar ? '40px' : '0'
        }} 
      />

      {/* Auth Modal */}
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}